/** Hook orchestrating email sign-in, sign-up, and tab state on the login page. */
import { useCallback, useState } from 'react';
import {
  resolveAuthErrorKey,
  signInWithEmail,
  signUpWithEmail,
} from '@/services/authService';
import {
  validateSignInFields,
  validateSignUpFields,
} from '@/features/auth/loginFormValidation';

export const MODE_SIGN_IN = 'signIn';
export const MODE_SIGN_UP = 'signUp';

const emptyFieldErrors = () => ({});

/**
 * @param {{ onPanelInteraction?: () => void }} [options]
 */
export function useLoginEmailAuth(options = {}) {
  const [mode, setMode] = useState(MODE_SIGN_IN);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [fieldErrors, setFieldErrors] = useState(emptyFieldErrors);
  const [serverErrorKey, setServerErrorKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  const resetSecrets = useCallback(() => {
    setPassword('');
    setPasswordConfirm('');
    setShowPassword(false);
    setShowConfirmPassword(false);
  }, []);

  const switchMode = (nextMode) => {
    if (nextMode === mode) {
      return;
    }
    setMode(nextMode);
    resetSecrets();
    setFieldErrors(emptyFieldErrors());
    setServerErrorKey(null);
    setForgotPasswordOpen(false);
    options.onPanelInteraction?.();
  };

  const applyClientValidation = (errors) => {
    setFieldErrors(errors);
    setServerErrorKey(null);
    return Object.keys(errors).length === 0;
  };

  const handleServerFailure = async (error) => {
    const key = await resolveAuthErrorKey(error, email);
    setServerErrorKey(key);
    resetSecrets();
  };

  const runSignIn = async () => {
    const errors = validateSignInFields({ email, password });
    if (!applyClientValidation(errors)) {
      return;
    }
    setLoading(true);
    try {
      await signInWithEmail(email, password);
    } catch (error) {
      await handleServerFailure(error);
    } finally {
      setLoading(false);
    }
  };

  const runSignUp = async () => {
    const errors = validateSignUpFields({ email, password, passwordConfirm });
    if (!applyClientValidation(errors)) {
      return;
    }
    setLoading(true);
    try {
      await signUpWithEmail(email, password);
    } catch (error) {
      await handleServerFailure(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignInSubmit = (event) => {
    event.preventDefault();
    if (loading) {
      return;
    }
    void runSignIn();
  };

  const handleSignUpSubmit = (event) => {
    event.preventDefault();
    if (loading) {
      return;
    }
    void runSignUp();
  };

  const validateSignInBlur = (field) => {
    const errors = validateSignInFields({ email, password });
    setFieldErrors((prev) => ({
      ...prev,
      [field]: errors[field],
    }));
  };

  const validateSignUpBlur = (field) => {
    const errors = validateSignUpFields({ email, password, passwordConfirm });
    setFieldErrors((prev) => ({
      ...prev,
      [field]: errors[field],
    }));
  };

  return {
    mode,
    email,
    password,
    passwordConfirm,
    fieldErrors,
    serverErrorKey,
    loading,
    showPassword,
    showConfirmPassword,
    setEmail,
    setPassword,
    setPasswordConfirm,
    setShowPassword,
    setShowConfirmPassword,
    switchMode,
    handleSignInSubmit,
    handleSignUpSubmit,
    validateSignInBlur,
    validateSignUpBlur,
    forgotPasswordOpen,
    openForgotPassword: () => setForgotPasswordOpen(true),
    closeForgotPassword: () => setForgotPasswordOpen(false),
  };
}
